import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Send, Mail, Users, TrendingUp, AlertTriangle, CheckCircle2, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

const gradeBadge = (g) => {
  const map = {
    amiral: 'bg-sky-500/20 text-sky-300 border-sky-500/40',
    capitaine: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    lieutenant: 'bg-slate-500/20 text-slate-300 border-slate-500/40',
  };
  return map[g] || map.lieutenant;
};

const InterviewTracking = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [remindingEmail, setRemindingEmail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/interview-tracking`);
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runCycle = async () => {
    if (!window.confirm('Envoyer les relances aux inactifs + le rapport hebdo à Paolo et JB ?')) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/interview-tracking/run-reminders`);
      if (data.skipped) { toast.info('Cycle non exécuté : ' + data.reason); return; }
      if (!data.report_sent) toast.error('Rapport non confirmé pour tous les destinataires');
      toast.info(`Cycle exécuté : ${data.reminders_sent} relance(s) envoyée(s) — rapport ${data.report_sent ? 'OK' : 'KO'}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur cycle');
    } finally {
      setRunning(false);
    }
  };

  const remindOne = async (email) => {
    if (!window.confirm(`Envoyer une relance individuelle à ${email} ?`)) return;
    setRemindingEmail(email);
    try {
      const { data: result } = await axios.post(`${API}/admin/interview-tracking/remind/${encodeURIComponent(email)}`);
      if (result.sent !== true) throw new Error('Envoi non confirmé. Consulter le journal SMTP.');
      toast.success(`Relance envoyée à ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Échec relance');
    } finally {
      setRemindingEmail(null);
    }
  };

  const stats = data?.stats || [];
  const totalStarted7 = data?.total_started_7j || 0;
  const totalPublished7 = data?.total_published_7j || 0;
  const totalStarted30 = data?.total_started_30j || 0;
  const totalPublished30 = data?.total_published_30j || 0;
  const inactifs = data?.inactifs_count || 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8" data-testid="interview-tracking-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="text-slate-400 hover:text-sky-400 mb-2"
              data-testid="btn-back-profile"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour au profil
            </Button>
            <h1 className="text-3xl md:text-4xl font-orbitron uppercase tracking-wider text-sky-400 flex items-center">
              <TrendingUp className="w-7 h-7 md:w-8 md:h-8 mr-3" />
              Tracking Auto-Interviews
            </h1>
            <p className="text-sm text-slate-500 mt-2 max-w-3xl">
              Objectif : 1 auto-interview lancée par jour ouvré et par rédacteur (mardi + vendredi 17h → relances automatiques aux inactifs).
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={load} disabled={loading}
              className="bg-slate-800/60 hover:bg-slate-700/80 text-slate-200 border border-slate-700"
              data-testid="btn-refresh-tracking">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button onClick={runCycle} disabled={running}
              className="bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/50"
              data-testid="btn-run-cycle">
              <Send className={`w-4 h-4 mr-2 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Envoi en cours...' : 'Lancer le cycle de relance maintenant'}
            </Button>
          </div>
        </div>

        {loading && (
          <Card className="bg-slate-950/40 border-slate-800/60">
            <CardContent className="py-12 text-center text-slate-500">
              <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-sky-400" />
              Chargement...
            </CardContent>
          </Card>
        )}

        {!loading && data && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <Card className="bg-sky-950/30 border-sky-800/40" data-testid="kpi-started-7j">
                <CardContent className="py-4 text-center">
                  <div className="text-xs uppercase tracking-wider text-sky-400 mb-1">Lancées 7j</div>
                  <div className="text-3xl font-bold text-sky-200">{totalStarted7}</div>
                </CardContent>
              </Card>
              <Card className="bg-emerald-950/30 border-emerald-800/40" data-testid="kpi-published-7j">
                <CardContent className="py-4 text-center">
                  <div className="text-xs uppercase tracking-wider text-emerald-400 mb-1">Publiées 7j</div>
                  <div className="text-3xl font-bold text-emerald-200">{totalPublished7}</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/40 border-slate-800/60" data-testid="kpi-started-30j">
                <CardContent className="py-4 text-center">
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Lancées 30j</div>
                  <div className="text-3xl font-bold text-slate-200">{totalStarted30}</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/40 border-slate-800/60" data-testid="kpi-published-30j">
                <CardContent className="py-4 text-center">
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Publiées 30j</div>
                  <div className="text-3xl font-bold text-slate-200">{totalPublished30}</div>
                </CardContent>
              </Card>
              <Card className={`${inactifs > 0 ? 'bg-red-950/30 border-red-800/40' : 'bg-emerald-950/30 border-emerald-800/40'}`} data-testid="kpi-inactifs">
                <CardContent className="py-4 text-center">
                  <div className={`text-xs uppercase tracking-wider mb-1 ${inactifs > 0 ? 'text-red-400' : 'text-emerald-400'}`}>Inactifs</div>
                  <div className={`text-3xl font-bold ${inactifs > 0 ? 'text-red-300' : 'text-emerald-200'}`}>{inactifs} / {stats.length}</div>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            <Card className="bg-slate-950/40 border-slate-800/60">
              <CardHeader>
                <CardTitle className="text-lg font-orbitron uppercase tracking-wider text-sky-400 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Détail par rédacteur
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 text-slate-400 uppercase text-xs tracking-wider">
                        <th className="text-left py-2 px-2">Rédacteur</th>
                        <th className="text-center py-2 px-2">Lancées 7j</th>
                        <th className="text-center py-2 px-2">Publiées 7j</th>
                        <th className="text-center py-2 px-2">Lancées 30j</th>
                        <th className="text-center py-2 px-2">Publiées 30j</th>
                        <th className="text-center py-2 px-2">Dernier lancement</th>
                        <th className="text-center py-2 px-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.map((s) => (
                        <tr key={s.user_id || s.email}
                          className={`border-b border-slate-900 ${s.needs_reminder ? 'bg-red-950/15' : 'bg-emerald-950/10'} hover:bg-slate-900/40`}
                          data-testid={`row-${s.email}`}>
                          <td className="py-3 px-2">
                            <div className="font-semibold text-slate-100">{s.name}</div>
                            <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                              {s.email}
                              <Badge variant="outline" className={`text-[10px] ${gradeBadge(s.grade)}`}>{s.grade}</Badge>
                            </div>
                          </td>
                          <td className={`text-center py-3 px-2 font-bold ${s.started_7j === 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                            {s.started_7j}
                          </td>
                          <td className="text-center py-3 px-2 text-slate-300">{s.published_7j}</td>
                          <td className="text-center py-3 px-2 text-slate-300">{s.started_30j}</td>
                          <td className="text-center py-3 px-2 text-slate-300">{s.published_30j}</td>
                          <td className="text-center py-3 px-2 text-xs text-slate-500">{formatDate(s.last_started_at)}</td>
                          <td className="text-center py-3 px-2">
                            {s.needs_reminder ? (
                              <Button size="sm" variant="outline"
                                onClick={() => remindOne(s.email)}
                                disabled={remindingEmail === s.email}
                                className="text-amber-400 border-amber-700/60 hover:bg-amber-500/10 text-xs"
                                data-testid={`btn-remind-${s.email}`}>
                                {remindingEmail === s.email ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Mail className="w-3 h-3 mr-1" />Relancer</>}
                              </Button>
                            ) : (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {stats.length === 0 && (
                  <div className="py-8 text-center text-slate-500">
                    Aucun rédacteur tracké pour l&apos;instant.
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="mt-6 p-4 bg-slate-900/40 border border-slate-800 rounded-lg flex items-start gap-3">
              <Calendar className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-slate-400 leading-relaxed">
                <strong className="text-slate-200">Cycle automatique :</strong> chaque mardi et vendredi à 17h heure de Paris,
                Auto-Edito envoie une relance individuelle à chaque rédacteur inactif sur les 7 derniers jours et un rapport
                agrégé à <code className="text-slate-300">pgaroscio@gmail.com</code> et <code className="text-slate-300">jbgiraud@mac.com</code>.
                <br />Exclus du tracking : Ludovic (contributeur externe) et Mathieu (lieutenant externe).
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InterviewTracking;
